import React, { useEffect, useState } from "react";
import { Collapse, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import PurchaseLayout from "./components/PurchaseLayout";
import LeftSubSection from "./components/LeftSubSection";
import ConfirmationModal from "../../../../common/components/ConfirmationModal";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  getBankAccount,
  postNewBankAccount,
  updateNewBankAccount,
  validateBvn,
  validateNin,
} from "../../../../../apis/buyer/payment";
import { BVN_LENGTH, NIN_LENGTH } from "../../../../common/utils/constants";
import {
  getEmploymentStatuses,
  getGendersList,
  getMeansOfIdentification,
  getNextOfKinRelationships,
  getSayYesNo,
} from "../../../../../apis/others";
import {
  getCountriesList,
  getCountryStatesList,
} from "../../../../../apis/locations";
import BrowseDeviceField from "../../../../common/components/BrowseDeviceField";
import DropdownAutocompleteField from "../../../../common/components/DropdownAutocompleteField";
import { constructFullName } from "../../../../common/utils/helpers";
import LivelinessCheckModal from "./components/LivelinessCheckModal";
import {
  getAuthListings,
  getListing,
} from "../../../../../apis/listings/buyer";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../store/store";
import { errorToastify } from "../../../../common/utils/Toasty";
import { LivelinessCheckBtn } from "../../../../common/components/LayoutIcons";
import LiveCheck from "../../OurTeam/components/LivelinessModal";

const steps = 2;

// Initial state for each step
const initialStep1 = {
  bvn: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  gender: "",
  dob: "",
  nationality: "",
  state_of_origin: "",
  government_issued_id: "",
  id_number: "",
  nin: "",
};
const initialStep2 = {
  nok_first_name: "",
  nok_middle_name: "",
  nok_last_name: "",
  nok_email: "",
  nok_phone_no: "",
  nok_gender: "",
  nok_dob: "",
  nok_relationship: "",
  nok_address: "",
  referee_first_name: "",
  referee_last_name: "",
  referee_email: "",
  referee_phone_no: "",
  referee_account_no: "",
  referee_bank: "",
  utility_bill: "",

  address_line_1: "",
  // address_line_2: "",
  // address_line_3: "",
  city: "",
  state: "",
  zip_postal_code: "",
  country_of_birth: "",
  country_of_residence: "",

  purpose_of_account: "",
  employment_status: "",
  employment_date: "",
  annual_income: "",
  company_name: "",
  company_address: "",
  company_country: "",
  company_location_zip_postal_code: "",

  is_US_resident: "",
  is_US_citizen: "",
  is_OECD_resident: "",

  signature: "",
  facematch_score: "",
};

const PurchaseAccountSetup = () => {
  const navigate = useNavigate();
  const { lid: listingId } = useParams();
  const listing = useSelector((state: RootState) => state.listings);
  const dispatch: AppDispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [step1Data, setStep1Data] = useState(initialStep1);
  const [step2Data, setStep2Data] =
    useState<Record<string, string>>(initialStep2);
  const [refId, setRefId] = useState("");
  const [signature, setSignature] = useState<File | null>();
  const [utitlityBill, setUtitlityBill] = useState<File | null>();
  const [errorFields, setErrorFields] = useState<Record<string, boolean>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [bvnFields, setBvnFields] = useState<Record<string, string>>({});
  const [bvnValidated, setBvnValidated] = useState(false);
  const [ninValidated, setNinValidated] = useState(false);
  const [livelinessScore, setLivelinessScore] = useState(0);
  const [showLivelinessCheckModal, setShowLivelinessCheckModal] =
    useState(false);

  useEffect(() => {
    if (listingId && listingId !== listing?.listing?.lid) {
      dispatch(getAuthListings(listingId));
    }
  }, [listingId, listing, dispatch]);

  useEffect(() => {
    if (livelinessScore) {
      setStep2Data((prevData) => ({
        ...prevData,
        facematch_score: livelinessScore.toString(),
      }));
    }
  }, [livelinessScore]);

  useEffect(() => {
    let foundCities: { id: number; name: string }[] = [];
    if (countryStates2?.length) {
      countryStates2.forEach((state) => {
        if (state.id.toString() === step2Data.state.toString()) {
          foundCities = state.cities;
        }
      });
    }
    setCities(foundCities);
  }, [step2Data.state]);

  const { data: bankAccount, isLoading: isBankAccountLoading } = useQuery({
    queryKey: ["bankAccount"],
    queryFn: getBankAccount,
  });

  if (bankAccount?.account_number) {
    navigate(`/purchase/checkout/${listingId}`);
  }

  const { data: sayYesNo = [], isLoading: isSayYesNoLoading } = useQuery({
    queryKey: ["sayYesNo"],
    queryFn: getSayYesNo,
  });

  const { data: meansOfIdentification = [], isLoading: isMoiLoading } =
    useQuery({
      queryKey: ["meansOfIdentification"],
      queryFn: getMeansOfIdentification,
    });

  const { data: genders = [], isLoading: isGendersLoading } = useQuery({
    queryKey: ["genders"],
    queryFn: getGendersList,
  });

  const { data: countries = [], isLoading: isCountriesLoading } = useQuery({
    queryKey: ["countries"],
    queryFn: getCountriesList,
  });

  const { data: countryStates = [], isLoading: isStatesLoading } = useQuery({
    queryKey: ["countries", { countryId: step1Data.nationality }],
    queryFn: getCountryStatesList,
  });

  const { data: countryStates2 = [], isLoading: isStates2Loading } = useQuery({
    queryKey: ["countries2", { countryId: step2Data.country_of_residence }],
    queryFn: getCountryStatesList,
  });

  const {
    data: employentStatuses = [],
    isLoading: isEmployentStatusesLoading,
  } = useQuery({
    queryKey: ["employentStatuses"],
    queryFn: getEmploymentStatuses,
  });

  const {
    data: nextOfKinRelationships = [],
    isLoading: isNextOfKinRelationshipsLoading,
  } = useQuery({
    queryKey: ["nextOfKinRelationships"],
    queryFn: getNextOfKinRelationships,
  });

  const bvnMutation = useMutation({
    mutationFn: validateBvn,
    onSuccess: (data) => {
      if (data !== "error") {
        const fields = {
          first_name: data?.firstName || "",
          middle_name: data?.middlename || "",
          last_name: data?.lastName || "",
          dob: data?.DoB ? new Date(data.DoB).toISOString().split("T")[0] : "",
          // dob: "2005-12-02",
          gender: (data?.Gender || "")?.substring(0, 1),
          phone_number: data?.phone || data?.phone1 || data?.phone2 || "",
        };
        setStep1Data({
          ...step1Data,
          // ...data,
          ...fields,
        });
        setBvnValidated(true);
        setBvnFields(fields);
        // TODO: Handle success, e.g., show a success message or auto-fill form fields.
      }
    },
    onError: (error) => {
      // TODO: Handle error, e.g., show an error message to the user.
    },
  });

  const ninMutation = useMutation({
    mutationFn: validateNin,
    onSuccess: (data) => {
      if (data !== "error") {
        setNinValidated(true);
        // TODO: Handle success, e.g., show a success message or auto-fill form fields.
      }
    },
    onError: (error) => {
      // TODO: Handle error, e.g., show an error message to the user.
    },
  });

  const sendNewBankAccount = useMutation({
    mutationFn: postNewBankAccount,
    onSuccess: (data) => {
      if (data.errors) {
        const errorFields_: Record<string, boolean> = {};
        for (const key of Object.keys(data.errors)) {
          errorFields_[key] = true;
        }
        setErrorFields(errorFields_);
      } else if (data !== "error") {
        setErrorFields({});
        setRefId(data?.reference_id);
        setStep((prev) => prev + 1);
      }
    },
    onError: (error) => {
      // TODO: Handle error, e.g., show an error message to the user.
    },
  });

  const sendUpdateBankAccount = useMutation({
    mutationFn: updateNewBankAccount,
    onSuccess: (data) => {
      if (data.errors) {
        const errorFields_: Record<string, boolean> = {};
        for (const key of Object.keys(data.errors)) {
          errorFields_[key] = true;
        }
        setErrorFields(errorFields_);
      } else if (data?.status === "rejected") {
        // errorToastify(
        //   "Account opening has been rejected by the bank. Try again later."
        // );
        //  do nothing
      } else if (data !== "error") {
        setShowSuccessModal(true);
      }
    },
    onError: (error) => {
      // TODO: Handle error, e.g., show an error message to the user.
    },
  });

  // Handlers for each step
  const handleStep1 = (
    e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
  ) => {
    // delete field from errorFields
    if (errorFields[e.target.name]) {
      delete errorFields[e.target.name];
    }

    if (e.target.name === "dob") {
      /* convert date to YYYY-MM-DD format */
      const date = new Date(e.target.value);
      e.target.value = date.toISOString().split("T")[0];
    }

    setStep1Data({ ...step1Data, [e.target.name]: e.target.value });
  };
  const handleStep2 = (
    e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
  ) => {
    // delete field from errorFields
    if (errorFields[e.target.name]) {
      delete errorFields[e.target.name];
    }
    if (e.target.name === "employment_date") {
      /* convert date to YYYY-MM-DD format */
      const date = new Date(e.target.value);
      e.target.value = date.toISOString().split("T")[0];
    }
    if (e.target.name === "nok_dob") {
      /* convert date to YYYY-MM-DD format */
      const date = new Date(e.target.value);
      e.target.value = date.toISOString().split("T")[0];
    }
    if (e.target.name === "utility_bill") {
      const target = e.target as HTMLInputElement;
      setUtitlityBill(target.files?.[0]);
    }
    if (e.target.name === "signature") {
      const target = e.target as HTMLInputElement;
      setSignature(target.files?.[0]);
    }

    setStep2Data({ ...step2Data, [e.target.name]: e.target.value });
  };

  // Validation for each step
  const isStep1Valid =
    Object.values(step1Data).every(
      (value) => (value.trim?.() || value) != ""
    ) &&
    bvnValidated &&
    ninValidated;
  const isStep2Valid = Object.entries(step2Data).every(([key, value]) => {
    return (value.trim?.() || value) != "";
  });

  const saveAndContinue = () => {
    console.log("------> step2Data", step2Data);
    setErrorFields({});
    if (step === steps) {
      if (refId) {
        const bodyFormData = new FormData();

        for (let key of Object.entries(step1Data) as [string, any][]) {
          bodyFormData.append(key[0], key[1]);
        }
        for (let key of Object.entries(step2Data) as [string, any][]) {
          if (key[0] === "utility_bill" && utitlityBill) {
            bodyFormData.append("utility_bill", utitlityBill);
          } else if (key[0] === "signature" && signature) {
            bodyFormData.append("signature", signature);
          } else {
            bodyFormData.append(key[0], key[1]);
          }
        }

        bodyFormData.append("reference_id", refId);

        sendUpdateBankAccount.mutate(bodyFormData);
      } else {
        console.log("Account creation data missing. Please restart.");
      }
    } else {
      sendNewBankAccount.mutate(step1Data);
    }
  };

  return (
    !bankAccount?.account_name && (
      <PurchaseLayout heading="Account setup">
        <div className="relative flex-1 flex flex-col gap-y-[15px] sm:min-w-[700px] rounded-[8px]">
          <Collapse in={step === 1} unmountOnExit>
            <div className="flex flex-col gap-y-[15px]">
              <LeftSubSection>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      BVN{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="bvn"
                      value={step1Data.bvn}
                      onChange={(e) => {
                        // Only allow alphanumeric, convert to uppercase, and restrict to 12 chars
                        let value = e.target.value
                          // .replace(/[^a-zA-Z0-9]/g, "")
                          .replace(/[^0-9]/g, "")
                          .toUpperCase();
                        if (value.length > BVN_LENGTH)
                          value = value.slice(0, BVN_LENGTH);
                        setStep1Data({ ...step1Data, bvn: value });
                      }}
                      className="standard_form_input"
                      style={{
                        letterSpacing: "4px",
                        borderColor: bvnValidated ? "green" : "red",
                      }}
                    />
                    <div className="text-right text-[#5E5E5E]">
                      Don't have a BVN?{" "}
                      <a
                        href="https://nibss-plc.com.ng/bank-verification-numberbvn/diaspora-enrolment/"
                        className="text-[#066D0E]"
                      >
                        Click here
                      </a>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">.</label>
                    <button
                      className="filled-button w-full"
                      onClick={() => bvnMutation.mutate({ bvn: step1Data.bvn })}
                      disabled={
                        bvnMutation.isPending ||
                        !step1Data.bvn ||
                        step1Data.bvn.length < BVN_LENGTH
                      }
                      style={{ height: 52 }}
                    >
                      {bvnMutation.isPending ? "VALIDATING..." : "VALIDATE"}
                      {/* <Loading show={true} />{" "} */}
                    </button>
                  </div>
                </div>
              </LeftSubSection>
              <LeftSubSection title="Basic information">
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      First name{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={step1Data.first_name}
                      onChange={handleStep1}
                      disabled={!!bvnFields.first_name}
                      className="standard_form_input"
                      style={
                        errorFields.first_name ? { borderColor: "red" } : {}
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">
                      Middle name{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="middle_name"
                      value={step1Data.middle_name}
                      onChange={handleStep1}
                      disabled={!!bvnFields.middle_name}
                      className="standard_form_input"
                      style={
                        errorFields.middle_name ? { borderColor: "red" } : {}
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">
                      Last name{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={step1Data.last_name}
                      onChange={handleStep1}
                      disabled={!!bvnFields.last_name}
                      className="standard_form_input"
                      style={
                        errorFields.last_name ? { borderColor: "red" } : {}
                      }
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      Gender{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <Select
                      name="gender"
                      value={step1Data.gender}
                      onChange={handleStep1}
                      className="standard_form_input"
                      style={errorFields.gender ? { borderColor: "red" } : {}}
                      disabled={!!bvnFields.gender}
                      IconComponent={ExpandMoreRoundedIcon}
                    >
                      {genders.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">
                      Date of birth{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={step1Data.dob}
                      onChange={handleStep1}
                      disabled={!!bvnFields.dob}
                      className="standard_form_input"
                      style={errorFields.dob ? { borderColor: "red" } : {}}
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      Email{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={step1Data.email}
                      onChange={handleStep1}
                      disabled={!!bvnFields.email}
                      className="standard_form_input"
                      style={errorFields.email ? { borderColor: "red" } : {}}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">
                      Phone number{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone_number"
                      value={step1Data.phone_number}
                      onChange={handleStep1}
                      disabled={!!bvnFields.phone_number}
                      className="standard_form_input"
                      placeholder="+23480123456789"
                      style={
                        errorFields.phone_number ? { borderColor: "red" } : {}
                      }
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      Nationality{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <DropdownAutocompleteField
                      id="nationality"
                      name="nationality"
                      onChange={handleStep1}
                      value={step1Data.nationality}
                      options={countries}
                      placeholder="Select country"
                      getOptionLabel={(option) => option.name || ""}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <span className="text-[16px] mr-[10px]">
                            {option.flag}
                          </span>{" "}
                          {option.name}
                        </li>
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">
                      State of origin{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <DropdownAutocompleteField
                      id="state_of_origin"
                      name="state_of_origin"
                      onChange={handleStep1}
                      value={step1Data.state_of_origin}
                      options={countryStates}
                      placeholder="Select state"
                      getOptionLabel={(option) => option.name || ""}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      Government-issued ID{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <Select
                      name="government_issued_id"
                      value={step1Data.government_issued_id}
                      onChange={handleStep1}
                      className="standard_form_input"
                      style={
                        errorFields.government_issued_id
                          ? { borderColor: "red" }
                          : {}
                      }
                      IconComponent={ExpandMoreRoundedIcon}
                    >
                      {meansOfIdentification.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">
                      ID number{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="id_number"
                      value={step1Data.id_number}
                      onChange={handleStep1}
                      className="standard_form_input"
                      style={
                        errorFields.id_number ? { borderColor: "red" } : {}
                      }
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label className="custom__label">
                      NIN{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="nin"
                      value={step1Data.nin}
                      onChange={(e) => {
                        // Only allow alphanumeric, convert to uppercase, and restrict to 12 chars
                        let value = e.target.value
                          // .replace(/[^a-zA-Z0-9]/g, "")
                          .replace(/[^0-9]/g, "")
                          .toUpperCase();
                        if (value.length > NIN_LENGTH)
                          value = value.slice(0, NIN_LENGTH);
                        setStep1Data({ ...step1Data, nin: value });
                      }}
                      className="standard_form_input"
                      style={{
                        letterSpacing: "4px",
                        borderColor: ninValidated ? "green" : "red",
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="custom__label">{". "}</label>
                    <button
                      className="filled-button w-full"
                      onClick={() => ninMutation.mutate({ nin: step1Data.nin })}
                      disabled={
                        ninMutation.isPending ||
                        !step1Data.nin ||
                        step1Data.nin.length < NIN_LENGTH
                      }
                      style={{ height: 52 }}
                    >
                      {ninMutation.isPending ? "VALIDATING..." : "VALIDATE"}
                    </button>
                  </div>
                </div>
              </LeftSubSection>
            </div>
          </Collapse>
          <Collapse in={step === 2} unmountOnExit>
            <LeftSubSection title="Address and country of residence">
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Address line 1{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="address_line_1"
                    value={step2Data.address_line_1}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.address_line_1 ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">Address Line 2 </label>
                  <input
                    type="text"
                    name="address_line_2"
                    value={step2Data.address_line_2}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.address_line_2 ? { borderColor: "red" } : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">Address Line 3 </label>
                  <input
                    type="text"
                    name="address_line_3"
                    value={step2Data.address_line_3}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.address_line_3 ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Country{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <DropdownAutocompleteField
                    id="country_of_residence"
                    name="country_of_residence"
                    onChange={handleStep2}
                    value={step2Data.country_of_residence}
                    options={countries}
                    placeholder="Select country"
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <span className="text-[16px] mr-[10px]">
                          {option.flag}
                        </span>{" "}
                        {option.name}
                      </li>
                    )}
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    State{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <DropdownAutocompleteField
                    id="state"
                    name="state"
                    onChange={handleStep2}
                    value={step2Data.state}
                    options={countryStates2}
                    placeholder="Select state"
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    City <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <DropdownAutocompleteField
                    id="city"
                    name="city"
                    onChange={handleStep2}
                    value={step2Data.city}
                    options={cities}
                    placeholder="Select city"
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Zip/postal code{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="zip_postal_code"
                    value={step2Data.zip_postal_code}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.zip_postal_code ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Country of birth{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <DropdownAutocompleteField
                    id="country_of_birth"
                    name="country_of_birth"
                    onChange={handleStep2}
                    value={step2Data.country_of_birth}
                    options={countries}
                    placeholder="Select country"
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <span className="text-[16px] mr-[10px]">
                          {option.flag}
                        </span>{" "}
                        {option.name}
                      </li>
                    )}
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Country of residence{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <DropdownAutocompleteField
                    id="country_of_residence"
                    name="country_of_residence"
                    onChange={handleStep2}
                    value={step2Data.country_of_residence}
                    options={countries}
                    placeholder="Select country"
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <span className="text-[16px] mr-[10px]">
                          {option.flag}
                        </span>{" "}
                        {option.name}
                      </li>
                    )}
                  />
                </div>
              </div>
            </LeftSubSection>
          </Collapse>
          <Collapse in={step === 2} unmountOnExit>
            <LeftSubSection title="Employment details">
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Purpose of account{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="purpose_of_account"
                    value={step2Data.purpose_of_account}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.purpose_of_account
                        ? { borderColor: "red" }
                        : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Employment status{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <Select
                    name="employment_status"
                    value={step2Data.employment_status}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.employment_status
                        ? { borderColor: "red" }
                        : {}
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                  >
                    {employentStatuses.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Employment date{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="date"
                    name="employment_date"
                    value={step2Data.employment_date}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.employment_date ? { borderColor: "red" } : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Annual income{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="annual_income"
                    value={step2Data.annual_income}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.annual_income ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Company name{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={step2Data.company_name}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.company_name ? { borderColor: "red" } : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Company address{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_address"
                    value={step2Data.company_address}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.company_address ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Country{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <DropdownAutocompleteField
                    id="company_country"
                    name="company_country"
                    onChange={handleStep2}
                    value={step2Data.company_country}
                    options={countries}
                    placeholder="Select country"
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <span className="text-[16px] mr-[10px]">
                          {option.flag}
                        </span>{" "}
                        {option.name}
                      </li>
                    )}
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Zip/postal code{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_location_zip_postal_code"
                    value={step2Data.company_location_zip_postal_code}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.company_location_zip_postal_code
                        ? { borderColor: "red" }
                        : {}
                    }
                  />
                </div>
              </div>
            </LeftSubSection>
          </Collapse>
          <Collapse in={step === 2} unmountOnExit>
            <LeftSubSection title="For international citizens">
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    US resident{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <Select
                    name="is_US_resident"
                    value={step2Data.is_US_resident}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.is_US_resident ? { borderColor: "red" } : {}
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                  >
                    {sayYesNo.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    US citizen{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <Select
                    name="is_US_citizen"
                    value={step2Data.is_US_citizen}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.is_US_citizen ? { borderColor: "red" } : {}
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                  >
                    {sayYesNo.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    OECD resident{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <Select
                    name="is_OECD_resident"
                    value={step2Data.is_OECD_resident}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.is_OECD_resident ? { borderColor: "red" } : {}
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                  >
                    {sayYesNo.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="flex-1">.</div>
              </div>
            </LeftSubSection>
          </Collapse>
          <Collapse in={step === 2} unmountOnExit>
            <LeftSubSection title="Next of kin's details">
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    First name{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="nok_first_name"
                    value={step2Data.nok_first_name}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.nok_first_name ? { borderColor: "red" } : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Middle name{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="nok_middle_name"
                    value={step2Data.nok_middle_name}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.nok_middle_name ? { borderColor: "red" } : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Last name{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="nok_last_name"
                    value={step2Data.nok_last_name}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.nok_last_name ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Email{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="email"
                    name="nok_email"
                    value={step2Data.nok_email}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={errorFields.nok_email ? { borderColor: "red" } : {}}
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Phone number{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="tel"
                    name="nok_phone_no"
                    value={step2Data.nok_phone_no}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.nok_phone_no ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Gender{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <Select
                    name="nok_gender"
                    value={step2Data.nok_gender}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={errorFields.nok_gender ? { borderColor: "red" } : {}}
                    IconComponent={ExpandMoreRoundedIcon}
                  >
                    {genders.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Date of birth{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="date"
                    name="nok_dob"
                    value={step2Data.nok_dob}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={errorFields.nok_dob ? { borderColor: "red" } : {}}
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Relationship with kin{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <Select
                    name="nok_relationship"
                    value={step2Data.nok_relationship}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.nok_relationship ? { borderColor: "red" } : {}
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                  >
                    {nextOfKinRelationships.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Residential address of kin{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="nok_address"
                    value={step2Data.nok_address}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.nok_address ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
            </LeftSubSection>
          </Collapse>
          <Collapse in={step === 2} unmountOnExit>
            <LeftSubSection title="Referee details">
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    First name{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="referee_first_name"
                    value={step2Data.referee_first_name}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.referee_first_name
                        ? { borderColor: "red" }
                        : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Last name{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="referee_last_name"
                    value={step2Data.referee_last_name}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.referee_last_name
                        ? { borderColor: "red" }
                        : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Email{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="email"
                    name="referee_email"
                    value={step2Data.referee_email}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.referee_email ? { borderColor: "red" } : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Phone number{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="tel"
                    name="referee_phone_no"
                    value={step2Data.referee_phone_no}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.referee_phone_no ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Account number of referee{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="referee_account_no"
                    value={step2Data.referee_account_no}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.referee_account_no
                        ? { borderColor: "red" }
                        : {}
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Bank of referee{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    name="referee_bank"
                    value={step2Data.referee_bank}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.referee_bank ? { borderColor: "red" } : {}
                    }
                  />
                </div>
              </div>
            </LeftSubSection>
          </Collapse>
          <Collapse in={step === 2} unmountOnExit>
            <LeftSubSection title="Signature and others">
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label className="custom__label">
                    Signature{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <BrowseDeviceField
                    id="signature"
                    name="signature"
                    className="standard_form_input"
                    onChange={handleStep2}
                    value={step2Data.signature}
                    errorFields={errorFields}
                    accept=".png,.jpeg,.jpg,.bmp,.pdf"
                  />
                </div>
                <div className="flex-1">
                  <label className="custom__label">
                    Utility bill{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                  </label>
                  <BrowseDeviceField
                    id="utility_bill"
                    name="utility_bill"
                    className="standard_form_input"
                    onChange={handleStep2}
                    value={step2Data.utility_bill}
                    errorFields={errorFields}
                    accept=".png,.jpeg,.jpg,.bmp,.pdf"
                  />
                </div>
              </div>
              <div className="purchase_form_field">
                <div className="flex-1">
                  <label
                    className="custom__label"
                    style={{ color: livelinessScore > 0 ? "green" : "red" }}
                  >
                    Liveliness check{" "}
                    <span className="text-[#CD0101] font-semibold">*</span>
                    <LivelinessCheckBtn
                      className="inline cursor-pointer ml-[15px]"
                      onClick={() => setShowLivelinessCheckModal(true)}
                    />
                  </label>
                  {/* <input
                    type="text"
                    name="facematch_score"
                    value={step2Data.facematch_score}
                    onChange={handleStep2}
                    className="standard_form_input"
                    style={
                      errorFields.facematch_score ? { borderColor: "red" } : {}
                    }
                  /> */}
                </div>
                <div className="flex-1">.</div>
              </div>
            </LeftSubSection>
          </Collapse>

          <div className="flex justify-end gap-x-[24px]">
            <button className="outline-button sm:max-h-[44px]">CANCEL</button>
            <button
              className="filled-button sm:max-h-[44px]"
              disabled={
                (step === 1 && !isStep1Valid) ||
                (step === 2 && !isStep2Valid) ||
                step > steps ||
                sendUpdateBankAccount.isPending
              }
              onClick={saveAndContinue}
            >
              SAVE & CONTINUE
            </button>
          </div>
          <ConfirmationModal
            showModal={showSuccessModal}
            onCloseModal={() => setShowSuccessModal(false)}
            headerText="Bank Account Successfully Created!"
            btnText="Continue"
            btnActtion={() => {
              navigate(`/purchase/checkout/${listingId}`);
            }}
          >
            <div>
              <div className="text-[16px] text-[#5E5E5E] font-[400]">
                Your new account has been successfully created. You can now
                manage your property purchase payment securely.
              </div>
              <div className="bg-[#066D0E14] p-[15px] flex flex-col gap-y-[20px] mt-[45px] rounded-[8px] items-center">
                <div>
                  <div className="text-[#CECECE]">Bank Name</div>
                  <div className="text-[#5E5E5E] text-[20px]">First Bank</div>
                </div>
                <div>
                  <div className="text-[#CECECE]">Account Holder</div>
                  <div className="text-[#5E5E5E] text-[20px]">
                    {constructFullName([
                      step1Data.first_name,
                      step1Data.middle_name,
                      step1Data.last_name,
                    ])}
                  </div>
                </div>
                <div className="w-[100%]">
                  <div className="text-[#CECECE]">Account Number</div>
                  <div className="text-[#066D0E] text-[20px] bg-[#FFFFFF] p-[10px] rounded-[8px]">
                    XXX - XXX - XXXX
                  </div>
                </div>
              </div>
            </div>
          </ConfirmationModal>
          {showLivelinessCheckModal && (
            <LiveCheck
              showModal={showLivelinessCheckModal}
              // onCloseModal={() => setShowLivelinessCheckModal(false)}
              onCloseModal={() => setShowLivelinessCheckModal(false)}
              _setScore={setLivelinessScore}
            />
          )}
          {/* {showLivelinessCheckModal && (
            <LivelinessCheckModal
              showModal={showLivelinessCheckModal}
              // onCloseModal={() => setShowLivelinessCheckModal(false)}
              onCloseModal={() => setShowLivelinessCheckModal(false)}
              _setScore={setLivelinessScore}
            />
          )} */}
        </div>
      </PurchaseLayout>
    )
  );
};

export default PurchaseAccountSetup;
